import PropTypes from 'prop-types';
import React, {
  useMemo,
  useState,
  useEffect,
  useContext,
  useCallback,
  createContext,
} from 'react';

import { useAuthContext } from 'src/auth/hooks';

// Storage key for persisting active jobs
const STORAGE_KEY = 'voolist_active_jobs';

// The runs a progress card follows, one of each kind, none to start with.
const NO_JOBS = {
  bulkEditBatchId: null,
  delistRelistBatchId: null,
  bulkArchiveBatchId: null,
  bulkDelistBatchId: null,
  inkfrogImportJobId: null,
};
const JOB_KEYS = Object.keys(NO_JOBS);

const hasAnyJob = (jobs) => JOB_KEYS.some((key) => Boolean(jobs[key]));

/**
 * The runs saved in this browser for this account, or none: runs another account saved, and runs older than an
 * hour (they're likely completed or abandoned), are not this account's to show.
 *
 * @param {string|null} accountId - the signed-in account, or null when nobody is
 */
const getStoredJobs = (accountId) => {
  if (!accountId) return NO_JOBS;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      const oneHourAgo = Date.now() - 60 * 60 * 1000;
      if (parsed.accountId === accountId && parsed.timestamp > oneHourAgo) {
        return JOB_KEYS.reduce((jobs, key) => ({ ...jobs, [key]: parsed[key] || null }), NO_JOBS);
      }
    }
  } catch (e) {
    console.error('Failed to parse stored jobs:', e);
  }
  return NO_JOBS;
};

// Helper to save to localStorage, with the account the runs belong to
const saveJobs = (jobs) => {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        ...jobs,
        timestamp: Date.now(),
      })
    );
  } catch (e) {
    console.error('Failed to save jobs to localStorage:', e);
  }
};

// Helper to clear localStorage
const clearStoredJobs = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    console.error('Failed to clear stored jobs:', e);
  }
};

// Create the context
export const JobProgressContext = createContext(null);

// Custom hook for using the context
export const useJobProgress = () => {
  const context = useContext(JobProgressContext);
  if (!context) {
    throw new Error('useJobProgress must be used within a JobProgressProvider');
  }
  return context;
};

// Provider component
export const JobProgressProvider = ({ children }) => {
  // The runs belong to the account that started them. `accountId` is that account, null when nobody is signed in,
  // and undefined while the app is still finding out who is.
  const { user, loading } = useAuthContext();
  const accountId = loading ? undefined : user?.uid ?? null;

  const [jobs, setJobs] = useState(() => ({ accountId, ...getStoredJobs(accountId) }));
  // Another account signed in, or the seller signed out: the runs on screen become that account's own saved runs.
  if (accountId !== undefined && accountId !== jobs.accountId) {
    setJobs({ accountId, ...getStoredJobs(accountId) });
  }
  const { bulkEditBatchId, delistRelistBatchId, bulkArchiveBatchId, bulkDelistBatchId, inkfrogImportJobId } = jobs;

  const setJob = useCallback((key, id) => setJobs((current) => ({ ...current, [key]: id })), []);
  const setBulkEditBatchId = useCallback((id) => setJob('bulkEditBatchId', id), [setJob]);
  const setDelistRelistBatchId = useCallback((id) => setJob('delistRelistBatchId', id), [setJob]);
  const setBulkArchiveBatchId = useCallback((id) => setJob('bulkArchiveBatchId', id), [setJob]);
  const setBulkDelistBatchId = useCallback((id) => setJob('bulkDelistBatchId', id), [setJob]);
  const setInkfrogImportJobId = useCallback((id) => setJob('inkfrogImportJobId', id), [setJob]);

  // Results dialog state for delist-relist
  const [delistRelistResults, setDelistRelistResults] = useState(null);
  const [delistRelistResultsOpen, setDelistRelistResultsOpen] = useState(false);

  // Results dialog state for bulk archive
  const [bulkArchiveResults, setBulkArchiveResults] = useState(null);
  const [bulkArchiveResultsOpen, setBulkArchiveResultsOpen] = useState(false);

  // Results dialog state for bulk delist
  const [bulkDelistResults, setBulkDelistResults] = useState(null);
  const [bulkDelistResultsOpen, setBulkDelistResultsOpen] = useState(false);

  // Callback to refresh listings - will be set by the listing page
  const [onListingsRefresh, setOnListingsRefresh] = useState(() => () => {});

  // Keep the signed-in account's runs in this browser, so a card survives a reload. Signed out, or an account with
  // no runs, nothing is kept. While the app is still finding out who is signed in, what is kept is left alone.
  useEffect(() => {
    if (jobs.accountId === undefined) return;
    if (jobs.accountId && hasAnyJob(jobs)) {
      saveJobs(jobs);
    } else {
      clearStoredJobs();
    }
  }, [jobs]);

  // Start a bulk edit job
  const startBulkEdit = useCallback((batchId) => {
    setBulkEditBatchId(batchId);
  }, [setBulkEditBatchId]);

  // Clear bulk edit job
  const clearBulkEdit = useCallback(
    (shouldRefresh = true) => {
      setBulkEditBatchId(null);
      if (shouldRefresh && onListingsRefresh) {
        onListingsRefresh();
      }
    },
    [onListingsRefresh, setBulkEditBatchId]
  );

  // Start a delist-relist job
  const startDelistRelist = useCallback((batchId) => {
    setDelistRelistBatchId(batchId);
  }, [setDelistRelistBatchId]);

  // Handle delist-relist job completion with results
  const handleDelistRelistComplete = useCallback((results) => {
    setDelistRelistResults(results);
    setDelistRelistResultsOpen(true);
  }, []);

  // Clear delist-relist job (called when progress panel is closed)
  const clearDelistRelist = useCallback(() => {
    setDelistRelistBatchId(null);
    // If there are results, show the results dialog
    if (delistRelistResults) {
      setDelistRelistResultsOpen(true);
    }
  }, [delistRelistResults, setDelistRelistBatchId]);

  // Close results dialog and clear everything
  const closeDelistRelistResults = useCallback(
    (shouldRefresh = true) => {
      setDelistRelistResultsOpen(false);
      setDelistRelistResults(null);
      setDelistRelistBatchId(null);
      if (shouldRefresh && onListingsRefresh) {
        onListingsRefresh();
      }
    },
    [onListingsRefresh, setDelistRelistBatchId]
  );

  // Start a bulk archive job
  const startBulkArchive = useCallback((batchId) => {
    setBulkArchiveBatchId(batchId);
  }, [setBulkArchiveBatchId]);

  // Handle bulk archive job completion with results
  const handleBulkArchiveComplete = useCallback((results) => {
    setBulkArchiveResults(results);
    setBulkArchiveResultsOpen(true);
  }, []);

  // Clear bulk archive job (called when progress panel is closed)
  const clearBulkArchive = useCallback(() => {
    setBulkArchiveBatchId(null);
    // If there are results, show the results dialog
    if (bulkArchiveResults) {
      setBulkArchiveResultsOpen(true);
    }
  }, [bulkArchiveResults, setBulkArchiveBatchId]);

  // Close bulk archive results dialog and clear everything
  const closeBulkArchiveResults = useCallback(
    (shouldRefresh = true) => {
      setBulkArchiveResultsOpen(false);
      setBulkArchiveResults(null);
      setBulkArchiveBatchId(null);
      if (shouldRefresh && onListingsRefresh) {
        onListingsRefresh();
      }
    },
    [onListingsRefresh, setBulkArchiveBatchId]
  );

  // Start a bulk delist job
  const startBulkDelist = useCallback((batchId) => {
    setBulkDelistBatchId(batchId);
  }, [setBulkDelistBatchId]);

  // Handle bulk delist job completion with results
  const handleBulkDelistComplete = useCallback((results) => {
    setBulkDelistResults(results);
    setBulkDelistResultsOpen(true);
  }, []);

  // Clear bulk delist job (called when progress panel is closed)
  const clearBulkDelist = useCallback(() => {
    setBulkDelistBatchId(null);
    // If there are results, show the results dialog
    if (bulkDelistResults) {
      setBulkDelistResultsOpen(true);
    }
  }, [bulkDelistResults, setBulkDelistBatchId]);

  // Close bulk delist results dialog and clear everything
  const closeBulkDelistResults = useCallback(
    (shouldRefresh = true) => {
      setBulkDelistResultsOpen(false);
      setBulkDelistResults(null);
      setBulkDelistBatchId(null);
      if (shouldRefresh && onListingsRefresh) {
        onListingsRefresh();
      }
    },
    [onListingsRefresh, setBulkDelistBatchId]
  );

  // Start an Inkfrog import job
  const startInkfrogImport = useCallback((jobId) => {
    setInkfrogImportJobId(jobId);
  }, [setInkfrogImportJobId]);

  // Clear Inkfrog import job
  const clearInkfrogImport = useCallback(
    (shouldRefresh = true) => {
      setInkfrogImportJobId(null);
      if (shouldRefresh && onListingsRefresh) {
        onListingsRefresh();
      }
    },
    [onListingsRefresh, setInkfrogImportJobId]
  );

  // Register a refresh callback (called by listing page when mounted)
  const registerRefreshCallback = useCallback((callback) => {
    setOnListingsRefresh(() => callback);
  }, []);

  // Check if any job is active
  const hasActiveJobs = Boolean(
    bulkEditBatchId || delistRelistBatchId || bulkArchiveBatchId || bulkDelistBatchId || inkfrogImportJobId
  );

  // Memoize the context value
  const contextValue = useMemo(
    () => ({
      // State
      bulkEditBatchId,
      delistRelistBatchId,
      bulkArchiveBatchId,
      bulkDelistBatchId,
      inkfrogImportJobId,
      delistRelistResults,
      delistRelistResultsOpen,
      bulkArchiveResults,
      bulkArchiveResultsOpen,
      bulkDelistResults,
      bulkDelistResultsOpen,
      hasActiveJobs,

      // Actions
      startBulkEdit,
      clearBulkEdit,
      startDelistRelist,
      handleDelistRelistComplete,
      clearDelistRelist,
      closeDelistRelistResults,
      startBulkArchive,
      handleBulkArchiveComplete,
      clearBulkArchive,
      closeBulkArchiveResults,
      startBulkDelist,
      handleBulkDelistComplete,
      clearBulkDelist,
      closeBulkDelistResults,
      startInkfrogImport,
      clearInkfrogImport,
      registerRefreshCallback,
    }),
    [
      bulkEditBatchId,
      delistRelistBatchId,
      bulkArchiveBatchId,
      bulkDelistBatchId,
      inkfrogImportJobId,
      delistRelistResults,
      delistRelistResultsOpen,
      bulkArchiveResults,
      bulkArchiveResultsOpen,
      bulkDelistResults,
      bulkDelistResultsOpen,
      hasActiveJobs,
      startBulkEdit,
      clearBulkEdit,
      startDelistRelist,
      handleDelistRelistComplete,
      clearDelistRelist,
      closeDelistRelistResults,
      startBulkArchive,
      handleBulkArchiveComplete,
      clearBulkArchive,
      closeBulkArchiveResults,
      startBulkDelist,
      handleBulkDelistComplete,
      clearBulkDelist,
      closeBulkDelistResults,
      startInkfrogImport,
      clearInkfrogImport,
      registerRefreshCallback,
    ]
  );

  return (
    <JobProgressContext.Provider value={contextValue}>{children}</JobProgressContext.Provider>
  );
};

JobProgressProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
